import {
    useQuery,
    UseQueryResult,
    useMutation,
    UseMutationResult,
    useQueryClient,
} from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import { parseMutationResponse, MutationResponse } from '../shared.types';
import {
    locationDetailsResponseSchema,
    locationFilesResponseSchema,
    locationLinksResponseSchema,
    flattenFiles,
    LocationDetailsResponse,
    DashboardFile,
    LocationLinksResponse,
} from './locationDashboard.types';

export type {
    LocationDetail,
    LocationDetailsResponse,
    DashboardFile,
    DashboardLink,
    LocationLinksResponse,
    LocationSummary,
    PieChartDatum,
    LocationsByProjectsResponse,
    OverviewTabResponse,
} from './locationDashboard.types';

// ---- Query keys ----

export const LOCATION_DETAILS_QUERY_KEY = 'LocationDashboardDetails';
export const LOCATION_FILES_QUERY_KEY = 'LocationDashboardFiles';
export const LOCATION_LINKS_QUERY_KEY = 'LocationDashboardLinks';

// ---- Load Location Details (details-filters POST) ----

const loadLocationDetails = async (locationKey: string): Promise<LocationDetailsResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-dashboard/details-filters`, {
            json: { locationKey },
        })
        .json();
    const parsed = locationDetailsResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('loadLocationDetails: response did not match schema', parsed.error);
        return response as LocationDetailsResponse;
    }
    return parsed.data;
};

export const useLoadLocationDetails = (
    locationKey: string | null | undefined
): UseQueryResult<LocationDetailsResponse> =>
    useQuery<LocationDetailsResponse>({
        queryKey: [LOCATION_DETAILS_QUERY_KEY, locationKey],
        queryFn: () => loadLocationDetails(locationKey!),
        enabled: !!locationKey,
    });

// ---- Load Location Files (load-files/{key} GET) ----

const loadLocationFiles = async (locationKey: string): Promise<DashboardFile[]> => {
    const response = await createPacerApi()
        .get(`${API_ROOT}location-dashboard/load-files/${locationKey}`)
        .json();
    const parsed = locationFilesResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('loadLocationFiles: response did not match schema', parsed.error);
        return [];
    }
    return flattenFiles(parsed.data);
};

export const useLoadLocationFiles = (
    locationKey: string | null | undefined
): UseQueryResult<DashboardFile[]> =>
    useQuery<DashboardFile[]>({
        queryKey: [LOCATION_FILES_QUERY_KEY, locationKey],
        queryFn: () => loadLocationFiles(locationKey!),
        enabled: !!locationKey,
        // No initialData: it would force status to 'success' immediately, making isPending
        // permanently false so the loading state never shows. Consumers default `data` to [].
    });

// ---- Load Location Links (load-links POST) ----

const loadLocationLinks = async (
    locationKey: string,
    projectIds: number[]
): Promise<LocationLinksResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-dashboard/load-links`, {
            json: { locationKey, projectIds },
        })
        .json();
    const parsed = locationLinksResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('loadLocationLinks: response did not match schema', parsed.error);
        return { links: [] };
    }
    return parsed.data;
};

const serializeProjectIds = (projectIds: number[]) =>
    projectIds.slice().sort((a, b) => a - b).join(',');

export const useLoadLocationLinks = (
    locationKey: string | null | undefined,
    projectIds: number[]
): UseQueryResult<LocationLinksResponse> =>
    useQuery<LocationLinksResponse>({
        queryKey: [LOCATION_LINKS_QUERY_KEY, locationKey, serializeProjectIds(projectIds)],
        queryFn: () => loadLocationLinks(locationKey!, projectIds),
        enabled: !!locationKey,
        refetchOnMount: 'always',
        // No initialData: it would force status to 'success' immediately, making isPending
        // permanently false so the loading state never shows. Consumer defaults `links` to [].
    });

// ---- Deactivate Project Resource (deactivate-project-resource POST) ----

const deactivateProjectResource = async (projectResourceId: number): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-dashboard/deactivate-project-resource`, {
            json: { projectResourceId },
        })
        .json();
    return parseMutationResponse(response);
};

export const useDeactivateProjectResource = (
    locationKey?: string,
    projectIds?: number[]
): UseMutationResult<MutationResponse, Error, number> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, number>({
        mutationFn: deactivateProjectResource,
        onSuccess: () => {
            if (locationKey) {
                queryClient.invalidateQueries({
                    queryKey: [
                        LOCATION_LINKS_QUERY_KEY,
                        locationKey,
                        serializeProjectIds(projectIds ?? []),
                    ],
                });
            }
        },
    });
};
